import React, { useEffect, useState } from "react";
import client from "@/api/client";
import { useBranch } from "@/context/BranchContext";
import { useAuth } from "@/context/AuthContext";
import { rupiah } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  TrendingUp, Wallet, ShoppingBag, AlertTriangle, PackageX, Trophy,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid,
} from "recharts";

function Kpi({ title, value, sub, icon: Icon, tone = "primary" }) {
  const tones = {
    primary: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    rose: "bg-rose-50 text-rose-600",
  };
  return (
    <Card className="p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-md">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm text-muted-foreground">{title}</div>
          <div className="mt-2 font-mono text-2xl font-semibold tracking-tight">{value}</div>
          {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
        </div>
        <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { effectiveBranch, currentBranchName, isAllBranch } = useBranch();
  const { user } = useAuth();
  const hideCost = ["branch_head", "cashier"].includes(user.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regionId, setRegionId] = useState("");
  const [regions, setRegions] = useState([]);

  useEffect(() => {
    if (!isAllBranch) return;
    client.get("/price-regions").then((r) => setRegions(r.data)).catch(() => {});
  }, [isAllBranch]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (effectiveBranch) params.branch_id = effectiveBranch;
    if (regionId) params.region_id = regionId;
    client
      .get("/dashboard", { params })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [effectiveBranch, regionId]);

  if (loading || !data) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-9 w-64" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-72" /><Skeleton className="h-72" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-in-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground">{currentBranchName} · Ringkasan hari ini & bulan ini</p>
        </div>
        {isAllBranch && (
          <Select value={regionId || "all"} onValueChange={(v) => setRegionId(v === "all" ? "" : v)}>
            <SelectTrigger className="w-56" data-testid="dashboard-region"><SelectValue placeholder="Semua Region" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Region</SelectItem>
              {regions.map((r) => <SelectItem key={r.region_id} value={r.region_id} data-testid={`dash-region-opt-${r.region_id}`}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi title="Pendapatan Hari Ini" value={rupiah(data.today.revenue)} sub={`${data.today.count} transaksi`} icon={Wallet} tone="primary" />
        {hideCost
          ? <Kpi title="Transaksi Hari Ini" value={data.today.count} sub="total struk" icon={ShoppingBag} tone="green" />
          : <Kpi title="Laba Bersih Hari Ini" value={rupiah(data.today.net_profit ?? data.today.profit)} sub={`HPP ${rupiah(data.today.cogs)}${data.today.returns ? ` · Retur ${rupiah(data.today.returns)}` : ""}${data.today.special_loss ? ` · Jual Khusus ${rupiah(data.today.special_loss)}` : ""}`} icon={TrendingUp} tone="green" />}
        <Kpi title="Pendapatan Bulan Ini" value={rupiah(data.month.revenue)} sub={hideCost ? undefined : `Laba bersih ${rupiah(data.month.net_profit ?? data.month.profit)}`} icon={ShoppingBag} tone="primary" />
        <Kpi title="Peringatan Stok" value={`${data.low_stock} rendah`} sub={`${data.out_stock} habis`} icon={data.out_stock > 0 ? PackageX : AlertTriangle} tone={data.out_stock > 0 ? "rose" : "amber"} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-5">
          <h3 className="font-heading text-lg font-semibold">{hideCost ? "Pendapatan (7 hari)" : "Pendapatan vs HPP (7 hari)"}</h3>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.trend}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563EB" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#059669" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} />
                <Tooltip formatter={(v) => rupiah(v)} />
                <Area type="monotone" dataKey="pendapatan" stroke="#2563EB" fill="url(#g1)" strokeWidth={2} name="Pendapatan" />
                {!hideCost && <Area type="monotone" dataKey="laba" stroke="#059669" fill="url(#g2)" strokeWidth={2} name="Laba" />}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="flex items-center gap-2 font-heading text-lg font-semibold">
            <Trophy className="h-5 w-5 text-amber-500" /> Produk Terlaris
          </h3>
          <div className="mt-4 h-64">
            {data.best_sellers.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.best_sellers} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => rupiah(v)} />
                  <Bar dataKey="revenue" fill="#2563EB" radius={[0, 4, 4, 0]} name="Pendapatan" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Belum ada penjualan</div>
            )}
          </div>
        </Card>
      </div>

      {data.branch_comparison?.length > 0 && (
        <Card className="p-5">
          <h3 className="font-heading text-lg font-semibold">Perbandingan Cabang (Bulan Ini)</h3>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.branch_comparison}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} />
                <Tooltip formatter={(v) => rupiah(v)} />
                <Bar dataKey="revenue" fill="#2563EB" radius={[4, 4, 0, 0]} name="Pendapatan" />
                <Bar dataKey="profit" fill="#059669" radius={[4, 4, 0, 0]} name="Laba" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {data.region_comparison?.length > 0 && (
        <Card className="p-5" data-testid="region-comparison">
          <h3 className="font-heading text-lg font-semibold">Perbandingan Region / Regional Manager (Bulan Ini)</h3>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.region_comparison}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} />
                <Tooltip formatter={(v) => rupiah(v)} />
                <Bar dataKey="revenue" fill="#7c3aed" radius={[4, 4, 0, 0]} name="Omzet" />
                <Bar dataKey="profit" fill="#059669" radius={[4, 4, 0, 0]} name="Laba" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}
    </div>
  );
}
